// Admin User Management — TanStack Query hook for GET /api/v1/admin/users.
//
// Backs AdminUsersPage: an email-search + status-filtered, paginated list
// of every account. Filters serialize into the query key so the cache
// differentiates between filter combinations (mirrors
// useAdminCreatorApplications).

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type AdminUserStatus = "all" | "blocked" | "creator" | "admin";

export type AdminUsersFilters = {
  search?: string;
  status: AdminUserStatus;
  page: number;
  page_size: number;
};

export const DEFAULT_USER_FILTERS: AdminUsersFilters = {
  status: "all",
  page: 1,
  page_size: 50,
};

export type UserListItem = {
  id: string;
  email: string;
  display_name: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  email_verified: boolean;
  creator_status: string;
  is_admin: boolean;
  is_blocked: boolean;
  blocked_until: string | null;
};

export type AdminUsersListResponse = {
  data: UserListItem[];
  page: number;
  page_size: number;
  has_next: boolean;
};

function buildQueryString(filters: AdminUsersFilters): string {
  const params = new URLSearchParams();
  if (filters.search) params.set("search", filters.search);
  if (filters.status) params.set("status", filters.status);
  params.set("page", String(filters.page));
  params.set("page_size", String(filters.page_size));
  return params.toString();
}

async function fetchAdminUsers(
  filters: AdminUsersFilters
): Promise<AdminUsersListResponse> {
  const qs = buildQueryString(filters);
  const response = await fetch(`/api/v1/admin/users?${qs}`, {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Admin users fetch failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      // body not JSON
    }
    throw new Error(message);
  }
  return (await response.json()) as AdminUsersListResponse;
}

export function useAdminUsers(filters: AdminUsersFilters) {
  return useQuery<AdminUsersListResponse, Error>({
    queryKey: ["admin-users", filters],
    queryFn: () => fetchAdminUsers(filters),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}
